/**
 * Utilidades para procesamiento de leads en el radar
 */

import { Lead } from '../types';
import { stages, stageMapping } from './mockData';
import { calculateLeadPosition, getBaseSizeForDensity } from './radarMath';

export interface ProcessedLead extends Lead {
  x: number;
  y: number;
  angle: number;
  effectiveStageIndex: number;
  stageColor: string;
  baseRadius: number;
  progressLine: ProgressLineData | null;
  totalInStage: number;
  ringPosition: number;
}

export interface ProgressLineData {
  nextStageIndex: number;
  nextX: number;
  nextY: number;
  progressX: number;
  progressY: number;
  color: string;
}

export interface RadarConfig {
  width: number;
  height: number;
  margin: { top: number; right: number; bottom: number; left: number };
  animationDuration: number;
  showTooltip: boolean;
  showLegend: boolean;
  showProgress: boolean;
  detailLevel: 'minimal' | 'basic' | 'standard' | 'detailed';
}

/**
 * Mapea el stage completo (1-8) a effectiveStageIndex (0-3) para visualización
 *
 * Mapeo:
 * - Stage 1-3 (Calificación):
 *   - score >= 70 → 0 (Calificado)
 *   - score < 70 → 2 (No Calificado)
 *   - sin score → 1 (Nuevo)
 * - Stage 4-8 (Ventas/Postventa):
 *   - Todos → 3 (En Proceso / Cliente)
 */
export function getEffectiveStageIndexByScore(stage: number, score?: number): number {
  // Etapa de calificación (stages 1-3)
  if (stage <= 3) {
    if (score === undefined) return 1; // Nuevo
    return score >= 70 ? 0 : 2; // Calificado : No Calificado
  }

  // Etapas de ventas/postventa (stages 4-8)
  return 3; // En Proceso / Cliente
}

/**
 * Calcula el índice efectivo de la etapa considerando auto-advance
 * @deprecated Use getEffectiveStageIndexByScore for better accuracy with scoring
 */
export const getEffectiveStageIndex = (stage: number, progress: number): number => {
  let effectiveStageIndex = (stageMapping[stage] || 1) - 1;

  // Auto-advance al 100%
  if (progress >= 1.0 && effectiveStageIndex < stages.length - 1) {
    effectiveStageIndex++;
  }

  return effectiveStageIndex;
};

/**
 * Obtiene todos los leads de la misma etapa
 */
export const getLeadsInSameStage = (leads: Lead[], targetStageIndex: number): Lead[] => {
  return leads.filter(l => {
    const effectiveStageIndex = getEffectiveStageIndex(l.stage, l.progress);
    return effectiveStageIndex === targetStageIndex;
  });
};

/**
 * Calcula los datos de la línea de progreso para un lead
 */
export const calculateProgressLine = (
  lead: Lead,
  effectiveStageIndex: number,
  angle: number,
  centerX: number,
  centerY: number
): ProgressLineData | null => {
  if (lead.progress <= 0 || lead.progress >= 1.0 || effectiveStageIndex >= stages.length - 1) {
    return null;
  }

  const nextStageIndex = effectiveStageIndex + 1;
  const currentRadius = 350 - (effectiveStageIndex * 80);
  const nextRadius = 350 - (nextStageIndex * 80);

  const currentX = centerX + currentRadius * Math.cos(angle);
  const currentY = centerY + currentRadius * Math.sin(angle);
  const nextX = centerX + nextRadius * Math.cos(angle);
  const nextY = centerY + nextRadius * Math.sin(angle);

  const progressX = currentX + (nextX - currentX) * lead.progress;
  const progressY = currentY + (nextY - currentY) * lead.progress;

  return {
    nextStageIndex,
    nextX,
    nextY,
    progressX,
    progressY,
    color: stages[nextStageIndex].mainColor
  };
};

/**
 * Procesa un array de leads para obtener sus posiciones y propiedades en el radar
 */
export const processLeadsForRadar = (leads: Lead[], config: RadarConfig): ProcessedLead[] => {
  const centerX = config.width / 2;
  const centerY = config.height / 2;
  const leadCount = leads.length;

  return leads.map((lead, index) => {
    const effectiveStageIndex = getEffectiveStageIndex(lead.stage, lead.progress);
    const stagePos = stages[effectiveStageIndex];
    const radius = 350 - (effectiveStageIndex * 80);

    // Obtener leads en la misma etapa para cálculos de densidad
    const leadsInSameStage = getLeadsInSameStage(leads, effectiveStageIndex);
    const totalInStage = leadsInSameStage.length;

    // Calcular posición con flujo radial perfecto
    const position = calculateLeadPosition(index, leadCount, radius, centerX, centerY);

    // Calcular línea de progreso
    const progressLine = calculateProgressLine(lead, effectiveStageIndex, position.angle, centerX, centerY);

    // Tamaño adaptativo basado en densidad
    const baseSizeForDensity = getBaseSizeForDensity(leadCount, totalInStage);

    return {
      ...lead,
      x: position.x,
      y: position.y,
      angle: position.angle,
      effectiveStageIndex,
      stageColor: stagePos.mainColor,
      baseRadius: baseSizeForDensity,
      progressLine,
      totalInStage,
      ringPosition: 0 // Todos en anillo principal
    };
  });
};

/**
 * Calcula la configuración adaptativa del canvas basada en viewport
 */
export const calculateAdaptiveConfig = (
  viewportWidth: number,
  viewportHeight: number,
  detailLevel: 'minimal' | 'basic' | 'standard' | 'detailed',
  userConfig?: Partial<RadarConfig>
): RadarConfig => {
  // Reservar espacio para header y stats bar (aprox 200px)
  const availableHeight = viewportHeight - 200;
  const availableWidth = viewportWidth; // 100% del ancho disponible

  // Canvas usa TODO el ancho disponible, altura basada en disponible
  const canvasWidth = availableWidth;
  const canvasHeight = availableHeight;

  return {
    width: canvasWidth,
    height: canvasHeight,
    margin: { top: 20, right: 20, bottom: 20, left: 20 },
    animationDuration: 0,
    showTooltip: true,
    showLegend: true,
    showProgress: true,
    detailLevel,
    ...userConfig
  };
};

/**
 * Procesa leads usando etapas dinámicas específicas del proceso
 */
export const processLeadsForDynamicRadar = (
  leads: Lead[],
  config: RadarConfig,
  customStages: any[]
): ProcessedLead[] => {
  const centerX = config.width / 2;
  const centerY = config.height / 2;
  const leadCount = leads.length;

  return leads.map((lead, index) => {
    // Usar effectiveStageIndex que ya viene del componente padre
    const effectiveStageIndex = (lead as any).effectiveStageIndex || 0;

    // Usar stage personalizado en lugar del mock
    const stagePos = customStages[effectiveStageIndex];
    const radius = stagePos ? stagePos.radius : 300;

    // Obtener leads en la misma etapa para cálculos de densidad
    const leadsInSameStage = leads.filter(l => (l as any).effectiveStageIndex === effectiveStageIndex);
    const totalInStage = leadsInSameStage.length;

    // Calcular posición con flujo radial perfecto
    const position = calculateLeadPosition(index, leadCount, radius, centerX, centerY);

    // Calcular línea de progreso usando los stages personalizados
    const progressLine = calculateProgressLineForDynamicStages(
      lead,
      effectiveStageIndex,
      position.angle,
      centerX,
      centerY,
      customStages
    );

    // Tamaño adaptativo basado en densidad
    const baseSizeForDensity = getBaseSizeForDensity(leadCount, totalInStage);

    return {
      ...lead,
      x: position.x,
      y: position.y,
      angle: position.angle,
      effectiveStageIndex,
      stageColor: stagePos ? stagePos.mainColor : '#6B7280',
      baseRadius: baseSizeForDensity,
      progressLine,
      totalInStage,
      ringPosition: 0
    };
  });
};

/**
 * Calcula línea de progreso para etapas dinámicas
 */
const calculateProgressLineForDynamicStages = (
  lead: Lead,
  effectiveStageIndex: number,
  angle: number,
  centerX: number,
  centerY: number,
  customStages: any[]
): ProgressLineData | null => {
  // Normalizar progress (de 0-100 a 0.0-1.0)
  const normalizedProgress = Math.min(lead.progress / 100, 1.0);

  // No mostrar barra si está completo o sin progreso
  if (normalizedProgress <= 0 || normalizedProgress >= 1.0) {
    return null;
  }

  // Para proceso de calificación divergente
  const isQualificationProcess = customStages.length === 3 &&
    customStages.some(stage => stage.id === 'nuevos');

  if (isQualificationProcess) {
    // Solo mostrar progreso para leads en "Nuevos" (índice 1)
    if (effectiveStageIndex !== 1) return null;

    // Determinar destino basado en scoring
    const score = lead.scoring?.total || 0;
    const targetStageIndex = score >= 70 ? 0 : 2; // Calificado (0) o No Calificado (2)
    const targetStage = customStages[targetStageIndex];
    const currentStage = customStages[1]; // Nuevos

    const currentX = centerX + Math.cos(angle) * currentStage.radius;
    const currentY = centerY + Math.sin(angle) * currentStage.radius;
    const nextX = centerX + Math.cos(angle) * targetStage.radius;
    const nextY = centerY + Math.sin(angle) * targetStage.radius;

    const progressX = currentX + (nextX - currentX) * normalizedProgress;
    const progressY = currentY + (nextY - currentY) * normalizedProgress;

    return {
      nextStageIndex: targetStageIndex,
      nextX,
      nextY,
      progressX,
      progressY,
      color: targetStage.mainColor || '#6B7280'
    };
  } else {
    // Proceso secuencial normal
    if (effectiveStageIndex >= customStages.length - 1) return null;

    const currentStage = customStages[effectiveStageIndex];
    const nextStage = customStages[effectiveStageIndex + 1];

    if (!currentStage || !nextStage) return null;

    const currentX = centerX + Math.cos(angle) * currentStage.radius;
    const currentY = centerY + Math.sin(angle) * currentStage.radius;
    const nextX = centerX + Math.cos(angle) * nextStage.radius;
    const nextY = centerY + Math.sin(angle) * nextStage.radius;

    const progressX = currentX + (nextX - currentX) * normalizedProgress;
    const progressY = currentY + (nextY - currentY) * normalizedProgress;

    return {
      nextStageIndex: effectiveStageIndex + 1,
      nextX,
      nextY,
      progressX,
      progressY,
      color: nextStage.mainColor || '#6B7280'
    };
  }
};